import * as stylex from '@stylexjs/stylex';

// Inline static tokens for StyleX compatibility
const space = {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    7: '28px',
    8: '32px',
    9: '36px',
    10: '40px',
    11: '44px',
    12: '48px',
    13: '52px',
    14: '56px',
    15: '60px',
    16: '64px',
    17: '68px',
    18: '72px',
    19: '76px',
    20: '80px',
};

const containerSizes = {
    1: '448px',
    2: '688px',
    3: '880px',
    4: '1136px',
    5: '1200px',
    6: '1400px',
};

export const containerStyles = stylex.create({
    simpleContainer: {
        padding: space[6],
        width: '100%',
        marginBottom: space[8],
    },
});

export const gradientStyles = stylex.create({
    gradientBorder: {
        position: 'relative',
        borderRadius: '16px',
        padding: space[1],
        background: 'linear-gradient(90deg, rgba(255,120,50,0) 0%, rgba(255,120,50,0.7) 50%, rgba(255,120,50,0) 100%)',
        backgroundSize: '200% 100%',
        animation: 'gradientMove 8s linear infinite',
        marginBottom: space[8],
    },
    gradientAnimation: {
        animationName: stylex.keyframes({
            '0%': { backgroundPosition: '0% 0%' },
            '100%': { backgroundPosition: '200% 0%' },
        }),
        animationDuration: '8s',
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
    },
    innerContent: {
        background: 'rgba(10, 10, 10, 0.8)',
        borderRadius: '15px',
        padding: space[6],
        backdropFilter: 'blur(10px)',
        width: '100%',
        height: '100%',
    },
});

export const socialMediaStyles = stylex.create({
    socialLinks: {
        display: 'flex',
        gap: space[6],
        marginTop: space[4],
        justifyContent: 'center',
    },
    socialLink: {
        color: '#fff',
        fontSize: '1.5rem',
        padding: space[2],
        borderRadius: '50%',
        width: 50,
        height: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        textDecoration: 'none',
        position: 'relative',
        overflow: 'hidden',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            transform: 'scale(1.15) rotate(5deg)',
            boxShadow: '0 5px 15px rgba(255, 120, 50, 0.3)',
        },
        ':before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
            opacity: 0,
            transform: 'scale(0.5)',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
        },
        ':hover:before': {
            opacity: 1,
            transform: 'scale(1)',
        },
        ':active': {
            transform: 'scale(0.95)',
        },
    },
    socialLinkAlt: {
        color: '#fff',
        fontSize: '1.5rem',
        padding: space[2],
        borderRadius: '12px',
        width: 50,
        height: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(10px)',
        textDecoration: 'none',
        position: 'relative',
        overflow: 'hidden',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            transform: 'translateY(-8px)',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2), 0 0 10px rgba(255, 120, 50, 0.2)',
        },
        ':after': {
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '100%',
            height: '3px',
            background: 'linear-gradient(90deg, rgba(255,120,50,0.7) 0%, rgba(255,200,150,0.7) 100%)',
            transform: 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.3s ease',
        },
        ':hover:after': {
            transform: 'scaleX(1)',
        },
        ':active': {
            transform: 'translateY(-3px)',
        },
    },
    spotifyHover: {
        ':hover': {
            color: '#1DB954',
            boxShadow: '0 5px 15px rgba(29, 185, 84, 0.3)',
        },
    },
    appleMusicHover: {
        ':hover': {
            color: '#FA57C1',
            boxShadow: '0 5px 15px rgba(250, 87, 193, 0.3)',
        },
    },
    youtubeHover: {
        ':hover': {
            color: '#FF0000',
            boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
        },
    },
    websiteHover: {
        ':hover': {
            color: '#64B5F6',
            boxShadow: '0 5px 15px rgba(100, 181, 246, 0.3)',
        },
    },
});

export const buttonStyles = stylex.create({
    listenNowButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: space[3],
        paddingBottom: space[3],
        paddingLeft: space[6],
        paddingRight: space[6],
        marginTop: space[6],
        marginBottom: space[6],
        backgroundColor: 'rgba(255, 120, 50, 0.8)',
        color: '#fff',
        borderRadius: '30px',
        fontWeight: 500,
        fontSize: '1.1rem',
        cursor: 'pointer',
        textDecoration: 'none',
        boxShadow: '0 4px 12px rgba(255, 120, 50, 0.3)',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Montserrat', sans-serif",
        ':hover': {
            backgroundColor: 'rgba(255, 140, 70, 0.9)',
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(255, 120, 50, 0.4)',
        },
        ':before': {
            content: '""',
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            borderRadius: '30px',
            boxShadow: '0 0 0 0 rgba(255, 120, 50, 0.7)',
            animation: 'pulse 2s infinite',
        },
    },
    pulse: {
        animationName: stylex.keyframes({
            '0%': {
                boxShadow: '0 0 0 0 rgba(255, 120, 50, 0.7)',
            },
            '70%': {
                boxShadow: '0 0 0 10px rgba(255, 120, 50, 0)',
            },
            '100%': {
                boxShadow: '0 0 0 0 rgba(255, 120, 50, 0)',
            },
        }),
        animationDuration: '2s',
        animationIterationCount: 'infinite',
    },
    buttonIcon: {
        marginRight: space[2],
        display: 'flex',
        alignItems: 'center',
    },
});

export const layoutStyles = stylex.create({
    container: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        maxWidth: containerSizes[6],
        margin: '0 auto',
        justifyContent: 'center',
        paddingLeft: space[4],
        paddingRight: space[4],
        '@media (max-width: 800px)': {
            flexDirection: 'column-reverse',
            alignItems: 'center',
            paddingLeft: '0',
            paddingRight: '0',
        },
    },
    left: {
        flex: '1 1 50%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: space[8],
        minWidth: 0,
        maxWidth: '600px',
        '@media (max-width: 800px)': {
            paddingTop: space[4],
            paddingBottom: space[8],
            paddingLeft: space[6],
            paddingRight: space[6],
            width: '100%',
        },
    },
    right: {
        flex: '1 1 50%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: space[8],
        minWidth: 0,
        maxWidth: '600px',
        '@media (max-width: 800px)': {
            paddingTop: space[8],
            paddingBottom: space[4],
            paddingLeft: space[6],
            paddingRight: space[6],
            width: '100%',
        },
    },
}); 